import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faChartColumn,
  faChartLine,
  faEarthAmericas,
  faSparkles,
  faUsersGear,
} from '@fortawesome/pro-regular-svg-icons';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { Z_INDEX } from '../layout/app-sidebar/constants';
import type { RbacClaim } from '../../../models/Rbac';
import {
  adminHubLimited,
  canSeeAdminHub,
  canSeeDataStudio,
  dataStudioNotPurchased,
  isV2User,
} from '../../utils/rbac';
import { getAppUrl, type AppId } from '../../utils/module-urls';
import './app-switcher.css';

interface AppDescriptor {
  id: AppId;
  label: string;
  subtitle: string;
  icon: IconDefinition;
}

const APPS: Record<AppId, AppDescriptor> = {
  admin_hub: {
    id: 'admin_hub',
    label: 'Admin Hub',
    subtitle: 'Settings & users',
    icon: faUsersGear,
  },
  market_analyzer: {
    id: 'market_analyzer',
    label: 'Market Analyzer',
    subtitle: 'Trends & insights',
    icon: faEarthAmericas,
  },
  data_studio: {
    id: 'data_studio',
    label: 'Data Studio',
    subtitle: 'Predictive Scoring Models',
    icon: faChartColumn,
  },
  sales_copilot: {
    id: 'sales_copilot',
    label: 'Sales Copilot',
    subtitle: 'AI-assisted deals',
    icon: faChartLine,
  },
  ai_sales_copilot: {
    id: 'ai_sales_copilot',
    label: 'AI Sales Copilot',
    subtitle: 'Offerings, Account AI & Workflows',
    icon: faSparkles,
  },
};

const MODULE_ORDER: Exclude<AppId, 'admin_hub'>[] = [
  'market_analyzer',
  'data_studio',
  'sales_copilot',
  'ai_sales_copilot',
];

/** The copilots in the order they win a tie, as in mk-admin-app's rbac.ts. */
const COPILOT_APPS: AppId[] = ['sales_copilot', 'ai_sales_copilot'];

interface AppSwitcherProps {
  rbac?: RbacClaim | null;
  tenantId?: string;
  isSuperKudu?: boolean;
}

/**
 * v2 RBAC topbar app switcher. Shows Admin Hub + every module the user has
 * been assigned. Selecting another app navigates via `window.location.href`.
 * The current app (Data Studio) is highlighted and clicking it is a no-op.
 *
 * Hidden entirely for v1 users (no `mkClaims.rbac` claim on JWT).
 */
export function AppSwitcher({ rbac, tenantId, isSuperKudu }: AppSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        triggerRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!rbac || !isV2User(rbac)) return null;

  const current = APPS.data_studio;
  const assigned = new Set<string>(rbac.assignedModules);
  const flags = { superKudu: !!isSuperKudu };

  // Admin Hub shows for Org Admins or Data Studio customers; Data Studio shows
  // for module holders or Org Admins on tenants that bought it, and greyed out
  // on tenants that didn't (RGI-3158). Market Analyzer and the copilots require
  // the module to be assigned. SuperKudu sees all.
  //
  // A holder of both copilots is offered one, MSI first, as in mk-admin-app's
  // `resolveApps` (RGI-2287).
  const copilot = COPILOT_APPS.find((m) => assigned.has(m));
  const apps: AppDescriptor[] = [
    ...(canSeeAdminHub(rbac, flags) ? [APPS.admin_hub] : []),
    ...MODULE_ORDER.filter((m) => {
      if (m === 'data_studio')
        return (
          canSeeDataStudio(rbac, flags) || dataStudioNotPurchased(rbac, flags)
        );
      if (isSuperKudu) return true;
      if (COPILOT_APPS.includes(m)) return m === copilot;
      return assigned.has(m);
    }).map((m) => APPS[m]),
  ];

  // "(limited access)": Data Studio standard users get a limited Admin nav.
  const limitedApps: Partial<Record<AppId, boolean>> = {
    admin_hub: adminHubLimited(rbac, flags),
  };
  const disabledApps: Partial<Record<AppId, boolean>> = {
    data_studio: dataStudioNotPurchased(rbac, flags),
  };

  const handleSelect = (app: AppDescriptor) => {
    if (disabledApps[app.id]) return;
    setIsOpen(false);
    if (app.id === current.id) return;
    const url = getAppUrl(app.id, { tenantId: tenantId ?? null });
    if (url) window.location.href = url;
  };

  const handleOpenInNewTab = (url: string) => {
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        id="topbar-app-switcher-trigger"
        className="app-switcher-trigger"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="app-switcher-trigger__label">
          {current.label}
          {limitedApps[current.id] && (
            <span className="app-switcher__limited"> (limited access)</span>
          )}
        </span>
        <FontAwesomeIcon
          icon={faCaretDown}
          className="app-switcher-trigger__chevron"
        />
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          id="topbar-app-switcher-menu"
          className="app-switcher-menu"
          style={{ zIndex: Z_INDEX.DROPDOWN_MENU }}
        >
          <div className="app-switcher-menu__label">Select Application</div>
          {apps.map((app) => {
            const isCurrent = app.id === current.id;
            const isDisabled = !!disabledApps[app.id];
            const url =
              isCurrent || isDisabled
                ? null
                : getAppUrl(app.id, { tenantId: tenantId ?? null });
            return (
              <div
                key={app.id}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
                className={`app-switcher-menu__item${
                  isDisabled ? ' app-switcher-menu__item--disabled' : ''
                }`}
                aria-disabled={isDisabled || undefined}
                title={
                  isDisabled
                    ? 'Data Studio is not included in your plan'
                    : undefined
                }
                data-current={isCurrent ? 'true' : undefined}
                onClick={() => handleSelect(app)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(app);
                  }
                }}
              >
                <span className="app-switcher-menu__icon">
                  <FontAwesomeIcon icon={app.icon} />
                </span>
                <span className="app-switcher-menu__text">
                  <span className="app-switcher-menu__name">
                    {app.label}
                    {limitedApps[app.id] && (
                      <span className="app-switcher__limited">
                        {' '}
                        (limited access)
                      </span>
                    )}
                  </span>
                  <span className="app-switcher-menu__subtitle">
                    {app.subtitle}
                  </span>
                </span>
                {url && (
                  <button
                    type="button"
                    className="app-switcher-menu__open"
                    aria-label={`Open ${app.label} in a new tab`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInNewTab(url);
                    }}
                  >
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
